/**
 * Carry the last chunk of a recording across a panel close.
 *
 * When the side panel or tab goes away, the final 30 seconds are still in the
 * recorder's buffer. flush({keepalive:true}) tries to get them out, but a
 * browser rejects a keepalive body over 64 KB and cancels an ordinary fetch as
 * the page unloads — so the end of the session, which is usually the part worth
 * watching, is usually the part that never arrives.
 *
 * The unload flush therefore also writes the chunk to disk, and the next app
 * boot uploads whatever is still there. Nothing on the server had to change for
 * this: a chunk is named by (replay, sequence), so re-uploading one that did
 * make it out overwrites it with the same bytes, and AdminReplayGet lists the
 * replay's S3 prefix rather than trusting chunk_count — a chunk that arrives
 * days late still appears in the viewer, in the right place.
 *
 * localStorage rather than IndexedDB or chrome.storage.local: the write has to
 * complete inside the pagehide handler, and both of those are asynchronous. A
 * page that is going away does not come back to finish them.
 *
 * PHI: a replay chunk is unmasked chart content (see masking.js — V1 records
 * readable text), so what sits on the clinician's disk is bounded to one chunk,
 * tied to the account that recorded it, taken rather than copied on the next
 * boot, dropped on logout, and ignored once stale.
 */

import { uploadChunk } from './uploader';

/**
 * Identity and body live in separate keys.
 *
 * The body is stored as-is: it is already JSON text, and re-stringifying a
 * megabyte of it on the unload path spends exactly the time the upload it is
 * backing up needs. It also leaves the identity cheap to read back, which is
 * what lets clearCachedChunk check whose tail it is about to delete without
 * parsing the whole thing.
 */
const META_KEY = 'replay_pending_chunk';
const BODY_KEY = 'replay_pending_chunk_body';

/**
 * Biggest body worth keeping, in characters.
 *
 * localStorage holds UTF-16, so this is ~2 MB of a quota that is typically 5 MB
 * for the whole origin — and the app keeps other things in there. A larger tail
 * is dropped exactly as it is dropped today, which is a better outcome than a
 * QuotaExceededError that also takes out whatever else is stored.
 */
const MAX_CACHED_CHARS = 1_000_000;

/**
 * Give up on a chunk this old. Well inside the server's 30-day retention, so
 * the replay it belongs to still exists, but short enough that chart content
 * does not sit on a disk indefinitely waiting for a panel that may never reopen.
 */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function readMeta() {
  try {
    const raw = window.localStorage.getItem(META_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    // Unreadable, disabled, or hand-edited: there is no usable chunk either way.
    return null;
  }
}

/** Keep one chunk for the next boot. Synchronous: callers are unloading. */
export function cacheChunk(apiClient, replayId, sequence, body) {
  try {
    if (!apiClient?.sessionEmail || replayId === null || replayId === undefined) return false;
    if (!body || body.length > MAX_CACHED_CHARS) return false;
    // Body first: a quota failure then leaves no identity claiming a body that
    // was never written.
    window.localStorage.setItem(BODY_KEY, body);
    window.localStorage.setItem(
      META_KEY,
      JSON.stringify({
        email: apiClient.sessionEmail,
        replayId,
        sequence,
        storedAt: Date.now(),
      }),
    );
    return true;
  } catch (e) {
    // Quota, disabled storage, private mode, a partitioned iframe. The chunk is
    // lost exactly as it is without this module — never worth failing a flush.
    clearCachedChunk();
    return false;
  }
}

/**
 * Drop the kept chunk.
 *
 * With a replay and sequence, only if those are the ones still kept. An upload
 * can finish long after a later close has replaced the tail — including a close
 * in a different tab, since localStorage is shared per origin while any
 * bookkeeping in this module is not — and it must not delete the newer one.
 */
export function clearCachedChunk(replayId, sequence) {
  try {
    if (replayId !== undefined) {
      const record = readMeta();
      if (!record || record.replayId !== replayId || record.sequence !== sequence) return;
    }
    window.localStorage.removeItem(META_KEY);
    window.localStorage.removeItem(BODY_KEY);
  } catch (e) {
    /* nothing to do */
  }
}

/**
 * Upload whatever the last close left behind. Resolves false when there was
 * nothing, or nothing usable.
 *
 * Taken before the attempt rather than after, so it gets exactly one try. A
 * retry loop would keep chart content on disk for as long as uploads kept
 * failing, and a chunk the server will never accept — its replay expired, or a
 * different account owns it — would be retried on every boot forever.
 */
export async function uploadCachedChunk(apiClient) {
  const record = readMeta();
  let body = null;
  try {
    body = window.localStorage.getItem(BODY_KEY);
  } catch (e) {
    body = null;
  }
  clearCachedChunk();
  if (!record || !body) return false;
  // Someone else's tail: only the account that recorded it can upload it, and
  // the server would reject it anyway.
  if (!apiClient?.sessionEmail || record.email !== apiClient.sessionEmail) return false;
  if (!(Date.now() - record.storedAt < MAX_AGE_MS)) return false;

  // Flagged as recovered so the server does not move this replay's end time to
  // now — it was recorded whenever the panel was last open.
  const stored = await uploadChunk(apiClient, record.replayId, record.sequence, body, {
    recovered: true,
  });
  if (stored) {
    console.log(`replay: recovered ${stored} bytes left by the previous session`);
  }
  return stored > 0;
}
